import React from 'react';
import useThemeCanvas, { pulseRgb } from '../useThemeCanvas';

const COUNT = 90;
const CONNECTION = 160;
const SPEED = 0.4;

export default function ParticlesTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.particles = Array.from({ length: COUNT }, () => {
        const depth = Math.random(); // 0 = far (small, slow), 1 = near (big, fast)
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * SPEED * 2 * (0.4 + depth),
          vy: (Math.random() - 0.5) * SPEED * 2 * (0.4 + depth),
          r: 1.2 + depth * 3,
          depth,
          twinklePhase: Math.random() * Math.PI * 2,
          twinkleFreq: 0.6 + Math.random() * 1.2,
        };
      });
    },
    draw: (ctx, w, h, state, t) => {
      ctx.clearRect(0, 0, w, h);
      const particles = state.particles || [];

      const { r: lr, g: lg, b: lb } = pulseRgb(palette, t, colorMode === 'cycling' ? 0.5 : 0.08);
      const baseAlpha = dark ? 0.9 : 0.75;

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION) {
            // Lines between near (deep) particles read stronger than far ones
            const depthFactor = 0.4 + 0.6 * Math.min(particles[i].depth, particles[j].depth);
            const alpha = (1 - dist / CONNECTION) * 0.7 * depthFactor;
            ctx.strokeStyle = `rgba(${lr},${lg},${lb},${alpha})`;
            ctx.lineWidth = 0.6 + 0.6 * depthFactor;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Glowing nodes — a soft bloom around each dot reads far more premium than
      // a flat circle. Glow only in dark mode (bloom is invisible on white).
      ctx.shadowColor = `rgba(${lr},${lg},${lb},0.9)`;
      for (const p of particles) {
        const twinkle = 0.7 + 0.3 * Math.sin(t * p.twinkleFreq + p.twinklePhase);
        const alpha = baseAlpha * (0.45 + 0.55 * p.depth) * twinkle;
        ctx.shadowBlur = dark ? 6 + 8 * p.depth : 0;
        ctx.fillStyle = `rgba(${lr},${lg},${lb},${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;
    },
  });

  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
